/**
 * Quote-Builder Template: Select services → Adjust quantities → See total
 *
 * This template provides a live quote building experience where:
 * - User selects services/items from a list
 * - Can adjust quantities for each selected item
 * - Running total updates in real-time
 * - Optional contact form to submit quote request
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';
import type { QuoteBuilderData, QuoteItem, TemplateFormField } from './types';

// =============================================================================
// QUOTE-BUILDER TEMPLATE
// =============================================================================

registerBlockRenderer('quote-builder', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const templateData = data as QuoteBuilderData;

  if (!templateData.items || templateData.items.length === 0) {
    console.warn('[quote-builder] No items provided');
    return;
  }

  const wrapper = createBlockWrapper('quote-builder');
  wrapper.classList.add('pms-template-quote-builder');

  const currency = templateData.currency || '$';

  // Internal state
  const state = {
    phase: 'building' as 'building' | 'contact' | 'submitted',
    selectedItems: new Map<string, { item: QuoteItem; quantity: number }>(),
    formValues: {} as Record<string, string>,
  };

  // Title
  if (templateData.title) {
    const title = document.createElement('h3');
    title.className = 'pms-quote-title';
    title.textContent = templateData.title;
    wrapper.appendChild(title);
  }

  // Main layout: items list + quote summary
  const layout = document.createElement('div');
  layout.className = 'pms-quote-layout';

  // Items list
  const itemsSection = document.createElement('div');
  itemsSection.className = 'pms-quote-items';

  // Group items by category if categories exist
  const categories = new Map<string, QuoteItem[]>();
  for (const item of templateData.items) {
    const cat = item.category || 'Services';
    if (!categories.has(cat)) {
      categories.set(cat, []);
    }
    categories.get(cat)!.push(item);
  }

  for (const [category, items] of categories) {
    if (categories.size > 1) {
      const catHeader = document.createElement('h4');
      catHeader.className = 'pms-quote-category';
      catHeader.textContent = category;
      itemsSection.appendChild(catHeader);
    }

    for (const item of items) {
      const itemEl = renderQuoteItem(item, currency, state, updateSummary);
      itemsSection.appendChild(itemEl);
    }
  }

  layout.appendChild(itemsSection);

  // Quote summary sidebar
  const summarySection = document.createElement('div');
  summarySection.className = 'pms-quote-summary';
  summarySection.innerHTML = `
    <h4 class="pms-quote-summary-title">Your Quote</h4>
    <div class="pms-quote-summary-empty">Select items to build your quote</div>
    <div class="pms-quote-breakdown" style="display: none;"></div>
    <div class="pms-quote-total" style="display: none;">
      <span class="pms-quote-total-label">Total</span>
      <span class="pms-quote-total-value"></span>
    </div>
    <button class="pms-quote-submit-btn" disabled>${escapeHtml(templateData.submitLabel || 'Request Quote')}</button>
  `;
  layout.appendChild(summarySection);

  wrapper.appendChild(layout);

  // Contact form section (hidden until submit clicked)
  const contactSection = document.createElement('div');
  contactSection.className = 'pms-quote-contact';
  contactSection.style.display = 'none';
  if (templateData.contactFields && templateData.contactFields.length > 0) {
    renderContactForm(contactSection, templateData.contactFields, templateData.submitLabel || 'Submit Quote Request');
  }
  wrapper.appendChild(contactSection);

  // Success message section
  const successSection = document.createElement('div');
  successSection.className = 'pms-quote-success';
  successSection.style.display = 'none';
  successSection.innerHTML = `
    <div class="pms-template-success">
      <div class="pms-template-success-icon">&#10003;</div>
      <h4>Quote Request Submitted</h4>
      <p>We'll get back to you shortly with your personalized quote.</p>
    </div>
  `;
  wrapper.appendChild(successSection);

  // Bind submit button
  const submitBtn = summarySection.querySelector('.pms-quote-submit-btn') as HTMLButtonElement;
  submitBtn.addEventListener('click', () => {
    if (templateData.contactFields && templateData.contactFields.length > 0) {
      // Show contact form
      state.phase = 'contact';
      layout.style.display = 'none';
      contactSection.style.display = '';
    } else {
      // Submit directly
      handleSubmit();
    }
  });

  container.appendChild(wrapper);

  function updateSummary(): void {
    const emptyEl = summarySection.querySelector('.pms-quote-summary-empty') as HTMLElement;
    const breakdownEl = summarySection.querySelector('.pms-quote-breakdown') as HTMLElement;
    const totalEl = summarySection.querySelector('.pms-quote-total') as HTMLElement;
    const totalValueEl = summarySection.querySelector('.pms-quote-total-value') as HTMLElement;

    if (state.selectedItems.size === 0) {
      emptyEl.style.display = '';
      breakdownEl.style.display = 'none';
      totalEl.style.display = 'none';
      submitBtn.disabled = true;
      return;
    }

    emptyEl.style.display = 'none';
    breakdownEl.style.display = '';
    totalEl.style.display = '';
    submitBtn.disabled = false;

    // Build breakdown
    let total = 0;
    let breakdownHtml = '';

    for (const entry of state.selectedItems.values()) {
      const lineTotal = entry.item.unitPrice * entry.quantity;
      total += lineTotal;

      const qtyLabel = entry.quantity > 1 ? ` x ${entry.quantity}` : '';

      breakdownHtml += `
        <div class="pms-quote-line">
          <span class="pms-quote-line-name">${escapeHtml(entry.item.title)}${qtyLabel}</span>
          <span class="pms-quote-line-price">${currency}${lineTotal.toFixed(2)}</span>
        </div>
      `;
    }

    breakdownEl.innerHTML = breakdownHtml;
    totalValueEl.textContent = `${currency}${total.toFixed(2)}`;
  }

  function renderContactForm(section: HTMLElement, fields: TemplateFormField[], submitLabel: string): void {
    section.innerHTML = `
      <h4 class="pms-quote-contact-title">Contact Details</h4>
      <p class="pms-quote-contact-intro">Please provide your contact information to receive your quote.</p>
      <form class="pms-quote-contact-form">
        <div class="pms-quote-contact-fields"></div>
        <div class="pms-quote-contact-actions">
          <button type="button" class="pms-quote-back-btn">Back to Quote</button>
          <button type="submit" class="pms-quote-final-submit">${escapeHtml(submitLabel)}</button>
        </div>
      </form>
    `;

    const fieldsContainer = section.querySelector('.pms-quote-contact-fields') as HTMLElement;
    for (const field of fields) {
      const fieldEl = renderFormField(field);
      fieldsContainer.appendChild(fieldEl);
    }

    // Back button
    const backBtn = section.querySelector('.pms-quote-back-btn') as HTMLButtonElement;
    backBtn.addEventListener('click', () => {
      state.phase = 'building';
      contactSection.style.display = 'none';
      layout.style.display = '';
    });

    // Submit handler
    const form = section.querySelector('form') as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      handleSubmit();
    });
  }

  function renderFormField(field: TemplateFormField): HTMLElement {
    const fieldWrapper = document.createElement('div');
    fieldWrapper.className = 'pms-quote-field';

    const label = document.createElement('label');
    label.className = 'pms-quote-field-label';
    label.textContent = field.label;
    if (field.required) {
      label.innerHTML += ' <span class="pms-template-required">*</span>';
    }
    fieldWrapper.appendChild(label);

    let input: HTMLElement;

    if (field.type === 'textarea') {
      input = document.createElement('textarea');
      (input as HTMLTextAreaElement).placeholder = field.placeholder || '';
      (input as HTMLTextAreaElement).required = field.required || false;
      input.className = 'pms-quote-input pms-quote-textarea';
    } else if (field.type === 'select') {
      input = document.createElement('select');
      input.className = 'pms-quote-input pms-quote-select';
      const defaultOpt = document.createElement('option');
      defaultOpt.value = '';
      defaultOpt.textContent = field.placeholder || 'Select...';
      input.appendChild(defaultOpt);
      for (const opt of (field.options || [])) {
        const option = document.createElement('option');
        if (typeof opt === 'string') {
          option.value = opt;
          option.textContent = opt;
        } else {
          option.value = opt.value;
          option.textContent = opt.label;
        }
        input.appendChild(option);
      }
    } else {
      input = document.createElement('input');
      (input as HTMLInputElement).type = field.type || 'text';
      (input as HTMLInputElement).placeholder = field.placeholder || '';
      (input as HTMLInputElement).required = field.required || false;
      input.className = 'pms-quote-input';
    }

    input.dataset['fieldId'] = field.id;
    input.addEventListener('input', (e) => {
      state.formValues[field.id] = (e.target as HTMLInputElement).value;
    });

    fieldWrapper.appendChild(input);
    return fieldWrapper;
  }

  function handleSubmit(): void {
    // Build quote data
    const quoteItems: { id: string; title: string; quantity: number; unitPrice: number; total: number }[] = [];
    let grandTotal = 0;

    for (const entry of state.selectedItems.values()) {
      const lineTotal = entry.item.unitPrice * entry.quantity;
      grandTotal += lineTotal;
      quoteItems.push({
        id: entry.item.id,
        title: entry.item.title,
        quantity: entry.quantity,
        unitPrice: entry.item.unitPrice,
        total: lineTotal,
      });
    }

    // Build action string
    const itemsStr = quoteItems.map(i => `${i.id}:${i.quantity}`).join(',');
    const formStr = Object.entries(state.formValues)
      .filter(([, v]) => v)
      .map(([key, v]) => `${key}=${encodeURIComponent(v)}`)
      .join(';');

    const actionString = `${templateData.submitAction}:items=${itemsStr};total=${grandTotal.toFixed(2)}${formStr ? ';' + formStr : ''}`;

    state.phase = 'submitted';
    layout.style.display = 'none';
    contactSection.style.display = 'none';
    successSection.style.display = '';

    if (onAction) {
      onAction(actionString);
    }
  }
});

function renderQuoteItem(
  item: QuoteItem,
  currency: string,
  state: { selectedItems: Map<string, { item: QuoteItem; quantity: number }> },
  onUpdate: () => void
): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pms-quote-item';
  el.dataset['itemId'] = item.id;

  const isSelected = state.selectedItems.has(item.id);
  if (isSelected) {
    el.classList.add('pms-quote-item-selected');
  }

  let html = `
    <div class="pms-quote-item-select">
      <label class="pms-browse-checkbox-wrapper">
        <input type="checkbox" class="pms-browse-checkbox" ${isSelected ? 'checked' : ''} />
        <span class="pms-browse-checkbox-mark"></span>
      </label>
    </div>
    <div class="pms-quote-item-info">
      <span class="pms-quote-item-title">${escapeHtml(item.title)}</span>
      ${item.description ? `<span class="pms-quote-item-desc">${escapeHtml(item.description)}</span>` : ''}
    </div>
    <div class="pms-quote-item-price">
      <span class="pms-quote-item-unit-price">${currency}${item.unitPrice.toFixed(2)}</span>
      ${item.unit ? `<span class="pms-quote-item-unit">${escapeHtml(item.unit)}</span>` : ''}
    </div>
  `;

  if (item.hasQuantity) {
    const defaultQty = item.defaultQuantity || 1;
    const min = item.minQuantity || 1;
    const max = item.maxQuantity || 99;
    html += `
      <div class="pms-quote-item-quantity" style="display: ${isSelected ? '' : 'none'};">
        <button type="button" class="pms-quote-qty-btn pms-quote-qty-minus" aria-label="Decrease">-</button>
        <input type="number" class="pms-quote-qty-input" value="${defaultQty}" min="${min}" max="${max}" />
        <button type="button" class="pms-quote-qty-btn pms-quote-qty-plus" aria-label="Increase">+</button>
      </div>
    `;
  }

  el.innerHTML = html;

  // Checkbox handler
  const checkbox = el.querySelector('.pms-browse-checkbox') as HTMLInputElement;
  const quantityEl = el.querySelector('.pms-quote-item-quantity') as HTMLElement | null;

  checkbox.addEventListener('change', () => {
    if (checkbox.checked) {
      const qty = quantityEl ? parseInt((quantityEl.querySelector('.pms-quote-qty-input') as HTMLInputElement).value, 10) : 1;
      state.selectedItems.set(item.id, { item, quantity: qty });
      el.classList.add('pms-quote-item-selected');
      if (quantityEl) quantityEl.style.display = '';
    } else {
      state.selectedItems.delete(item.id);
      el.classList.remove('pms-quote-item-selected');
      if (quantityEl) quantityEl.style.display = 'none';
    }
    onUpdate();
  });

  // Quantity handlers
  if (quantityEl) {
    const qtyInput = quantityEl.querySelector('.pms-quote-qty-input') as HTMLInputElement;
    const minusBtn = quantityEl.querySelector('.pms-quote-qty-minus') as HTMLButtonElement;
    const plusBtn = quantityEl.querySelector('.pms-quote-qty-plus') as HTMLButtonElement;

    const updateQty = (newVal: number) => {
      const min = parseInt(qtyInput.min, 10);
      const max = parseInt(qtyInput.max, 10);
      newVal = Math.max(min, Math.min(max, newVal));
      qtyInput.value = String(newVal);
      if (state.selectedItems.has(item.id)) {
        state.selectedItems.get(item.id)!.quantity = newVal;
        onUpdate();
      }
    };

    minusBtn.addEventListener('click', () => updateQty(parseInt(qtyInput.value, 10) - 1));
    plusBtn.addEventListener('click', () => updateQty(parseInt(qtyInput.value, 10) + 1));
    qtyInput.addEventListener('change', () => updateQty(parseInt(qtyInput.value, 10)));
  }

  // Click on row toggles checkbox
  el.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('button, input')) return;
    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change'));
  });

  return el;
}
